import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { Formatter } from '../format';
import { MAX_QUOTES } from '../common/evidencePayload';
import { capQuote, citeFromLine, evidenceKey, markParts, matchLines } from '../common/citeQuote';
import { readSelection } from '../common/textSelection';

/*
 * "Cite a line": every line of the call, searchable, each with a Cite button. Cite quotes the whole line, or —
 * when words in it are selected — only those words (the button keeps the selection by cancelling its own
 * mousedown). On a translated line the English shows under the original and its words can be selected too.
 * The picker stays open after each Cite so several lines can be cited in a row; Done, the close button,
 * Escape or a click outside close it, and the editor puts focus back on the question's Cite a line button.
 *
 * Its own portal dialog rather than the shared Modal: it needs to be wide, named for its question, and
 * closable from the backdrop.
 *
 *   taken: evidenceKey()s already on the question (whole lines among them read "Cited"); a Cite that would add
 *     one again, or that holds no words, says so in the footer instead
 *   count: quotes on the question now (kept + cited); at MAX_QUOTES nothing more can be cited
 *   onCite({ quote, start, seg, source: 'human', speaker }) — `speaker` is for display; it is never sent
 */

const Marked = ({ text, query }) => markParts(text, query).map((part, i) => (part.hit
  ? <mark key={i} className="rounded bg-amber-100 px-0.5 text-gray-900">{part.text}</mark>
  : <React.Fragment key={i}>{part.text}</React.Fragment>));

const EvidencePicker = ({ question, segments = [], speakerLabels = {}, taken = [], count = 0, onCite, onClose }) => {
  const id = useId();
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const search = useRef(null);
  const dialog = useRef(null);
  const texts = useRef({});
  const held = useRef(null);
  const takenKeys = useMemo(() => new Set(taken), [taken]);
  const full = count >= MAX_QUOTES;
  const speakerOf = (segment) => speakerLabels[segment?.SegmentSpeaker] || segment?.SegmentSpeaker || '';
  const rows = matchLines(segments, query, speakerOf);

  useEffect(() => { if (search.current) search.current.focus(); }, []);

  const linesOf = (seg) => [texts.current[`${seg}-text`], texts.current[`${seg}-english`]];
  // A mousedown would move focus to the button and clear the selection; cancelling it keeps the words.
  const keepSelection = (seg) => (e) => {
    e.preventDefault();
    held.current = { seg, selection: readSelection(linesOf(seg)) };
  };
  const cite = (seg, segment) => {
    const kept = held.current && held.current.seg === seg ? held.current.selection : null;
    held.current = null;
    const selection = kept || readSelection(linesOf(seg));
    const citation = selection
      ? citeFromLine(segment, seg, { field: selection.index === 1 ? 'TranslatedText' : 'DisplayText', from: selection.from, to: selection.to })
      : citeFromLine(segment, seg);
    if (!citation) setNotice('There are no words to quote there.');
    else if (takenKeys.has(evidenceKey(citation))) setNotice('That quote is already on this question.');
    else {
      setNotice('');
      onCite({ ...citation, speaker: speakerOf(segment) });
    }
    if (window.getSelection) window.getSelection().removeAllRanges();
  };

  // Escape and Tab are heard on the document, as components/Modal.js listens: selecting words by dragging over a
  // line moves focus to the body, where a listener on the dialog never hears them. Tab from outside comes back in,
  // and so does Tab from the dialog itself (it takes focus when its text is clicked). Heard first, in the capture
  // phase, so Escape closes the picker alone and not, say, the chat drawer behind it.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !dialog.current) return;
      const stops = [...dialog.current.querySelectorAll('button:not([disabled]), input:not([disabled])')];
      if (!stops.length) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (active === dialog.current || !dialog.current.contains(active)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  return createPortal(
    <div
      data-testid="evidence-picker-backdrop"
      className="fixed inset-0 z-[90] flex items-start justify-center bg-black bg-opacity-40 p-4 pt-[8vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-for ${id}-question`}
        tabIndex={-1}
        className="flex max-h-[84vh] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0 flex-1">
            <p id={`${id}-for`} className="text-xs font-medium text-gray-500">Cite a line for</p>
            <h3 id={`${id}-question`} className="text-base font-semibold text-gray-900">{question}</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <FaTimes className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-1.5 px-5 pt-3">
          <input
            ref={search}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the transcript"
            aria-label="Search the transcript"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-500">Press Cite to quote a whole line. To quote only part of one, select those words first.</p>
        </div>

        {rows.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-500">No line matches “{query.trim()}”.</p>
        ) : (
          <ol className="mt-2 flex-1 overflow-y-auto px-3 pb-2">
            {rows.map(({ seg, segment, speaker }) => {
              const time = Formatter.Time(Number(segment?.SegmentStartTime) || 0);
              const cited = takenKeys.has(evidenceKey({ source: 'human', quote: capQuote(segment?.DisplayText), seg }));
              return (
                <li key={seg} className="grid grid-cols-[46px_minmax(0,1fr)_auto] items-start gap-x-3 rounded-lg px-2 py-2 hover:bg-gray-50">
                  <span className="pt-0.5 text-xs tabular-nums text-gray-500">{time}</span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold text-gray-500">{speaker}</p>
                    <p ref={(el) => { texts.current[`${seg}-text`] = el; }} className="break-words text-sm text-gray-800">
                      <Marked text={segment?.DisplayText || ''} query={query} />
                    </p>
                    {segment?.TranslatedText && (
                      <p ref={(el) => { texts.current[`${seg}-english`] = el; }} className="mt-0.5 break-words text-xs italic text-gray-500">
                        <Marked text={segment.TranslatedText} query={query} />
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onMouseDown={keepSelection(seg)}
                    onClick={() => cite(seg, segment)}
                    disabled={full || cited}
                    aria-label={cited ? `Cited: ${speaker} at ${time}` : `Cite ${speaker} at ${time}`}
                    className="btn-outline px-2.5 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {cited ? 'Cited' : 'Cite'}
                  </button>
                </li>
              );
            })}
          </ol>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-5 py-3">
          <p className="text-xs text-gray-600">
            {count} of {MAX_QUOTES} quotes on this question.{full ? ' Remove one to cite another.' : ''}
          </p>
          <p role="status" className="min-w-0 flex-1 text-xs font-medium text-amber-800">{notice}</p>
          <button type="button" onClick={onClose} className="btn-secondary px-3 py-1.5 text-sm">Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EvidencePicker;
